// Special character used to designate letter boxes which have been revealed
// as being empty
export const REVEALED_EMPTY_CHAR = "!";

// Special character used to designate letters that have not yet been
// revealed.
export const UNKNOWN_LETTER_CHAR = "?";

/*
 * These are helper functions for working with the player spelling string in the
 * OneChance round. The string is formatted using specific syntax encodes data
 * about their progress. Here's an example:
 *
 * adroNe??I?!!????????
 *
 * ? = An unknown letter. This is a character that has not been typed by the
 *     player or revealed through clues system. At the beginning of the round
 *     the string consists of 20 of these.
 *
 * lowercase char = User-typed letter. This is a letter that was typed by the
 *                  player. It may or not be correct relative to the actual
 *                  word.
 *
 * uppercase char = Correct letter revealed with the clue system.
 *
 * ! = A space that was revealed to be empty with the clue system. If the correct
 *     spelling is less than 20 characters, the remainder of the string will
 *     consist of these when revealed.
 */

// Parse just the user's intended spelling out of the string.
export function parseUserSpelling(currentSpelling: string): string {
  let userSpelling = "";

  for (let i = 0; i < currentSpelling.length; i++) {
    const char = currentSpelling[i];
    if (char === UNKNOWN_LETTER_CHAR) continue;
    if (char === REVEALED_EMPTY_CHAR) break;
    userSpelling += char.toLowerCase();
  }

  return userSpelling;
}

// Returns true if the cursor can be placed at the desired position.
export function checkIsValidCaretPos(
  currentSpelling: string,
  desiredPos: number,
): boolean {
  // Check whether the caret is in a valid position.
  if (desiredPos < 0 || desiredPos > getMaxCaretPos(currentSpelling)) {
    return false;
  }

  const char = currentSpelling[desiredPos];
  return (
    char !== REVEALED_EMPTY_CHAR &&
    (char === UNKNOWN_LETTER_CHAR || char === char.toLowerCase())
  );
}

// Determines the highest valid position of the caret in [currentSpelling].
export function getMaxCaretPos(currentSpelling: string): number {
  // Count backwards from the final cell, and return the location of the
  // first editable character.
  for (let i = currentSpelling.length; i >= 0; i--) {
    if (checkIsEditable(currentSpelling[i])) {
      return i;
    }
  }
  
  return 0;

  // The commented-out functionality below prevent the player from moving the
  // cursor further than one cell after the most recent letter, mimicking the
  // behavior of typing into an input element. I'm leaving it here in case we
  // decide to switch back at any point.

  // // First, get the furthest letter (revealed or typed)
  // let furthestLetterIndex = -1;
  // for (let i = currentSpelling.length - 1; i >= 0; i--) {
  //   const isLetter =
  //     currentSpelling[i] !== UNKNOWN_LETTER_CHAR &&
  //     currentSpelling[i] !== REVEALED_EMPTY_CHAR;
  //   if (isLetter) {
  //     furthestLetterIndex = i;
  //     break;
  //   }
  // }

  // if (furthestLetterIndex === -1) {
  //   return 0;
  // }

  // // If the space following that letter is open, place the cursor there
  // const isNextSpaceOpen =
  //   furthestLetterIndex <= currentSpelling.length - 1 &&
  //   currentSpelling[furthestLetterIndex + 1] === UNKNOWN_LETTER_CHAR;
  // if (isNextSpaceOpen) {
  //   return furthestLetterIndex + 1;
  // }

  // // Otherwise, count backwards until we find the next closest editable space
  // for (let i = furthestLetterIndex; i >= 0; i--) {
  //   if (checkIsEditable(currentSpelling[i])) {
  //     return i;
  //   }
  // }

  // return 0;
}

// Returns an index corresponding to the character position closest to
// [desiredPos] where the cursor can validly be placed. If [desiredPos] itself
// is not valid, it will first try moving the caret forward, and then backward.
export function tryPlaceCaret(
  currentSpelling: string,
  desiredPos: number,
): number {
  desiredPos = Math.max(0, Math.min(desiredPos, currentSpelling.length - 1));

  if (checkIsValidCaretPos(currentSpelling, desiredPos)) {
    return desiredPos;
  }

  // First try to move it forward
  const forwardPos = tryMoveCaret(currentSpelling, desiredPos, 1);
  if (forwardPos !== desiredPos) {
    return forwardPos;
  }

  // If that failed, try moving it back
  const backPos = tryMoveCaret(currentSpelling, desiredPos, -1);
  return backPos;
}

// Returns the new position of the caret if it moved through the string to the
// next editable character. Dir must be either -1 or 1, corresponding to the
// desired movement direction.
export function tryMoveCaret(
  currentSpelling: string,
  from = 0,
  dir = 1,
): number {
  dir = Math.sign(dir);

  let newPosition = from + dir;
  while (newPosition >= 0 && newPosition <= currentSpelling.length) {
    if (checkIsValidCaretPos(currentSpelling, newPosition)) {
      return newPosition;
    }
    newPosition += dir;
  }

  // If no space was found, return the original position.
  return from;
}

export function revealChar(
  currentSpelling: string,
  correctSpelling: string,
  revealPos: number,
): string {
  if (checkIsRevealed(currentSpelling[revealPos])) return currentSpelling;

  let newString = currentSpelling;

  const revealedChar =
    revealPos < correctSpelling.length
      ? correctSpelling[revealPos].toUpperCase()
      : REVEALED_EMPTY_CHAR;

  newString =
    newString.slice(0, revealPos) +
    revealedChar +
    newString.slice(revealPos + 1);

  // If this character is empty, check all the ones to its right, and reveal
  // them if they're also empty.
  if (revealedChar === REVEALED_EMPTY_CHAR) {
    for (let i = revealPos + 1; i < newString.length; i++) {
      const isEmpty = i >= correctSpelling.length;
      if (isEmpty) {
        newString =
          newString.slice(0, i) + REVEALED_EMPTY_CHAR + newString.slice(i + 1);
      }
    }
  }

  return newString;
}

// Returns the player's spelling modified to indicate the length of correctSpelling.
export function revealLength(
  currentSpelling: string,
  correctSpelling: string,
): string {
  let newSpelling = "";

  for (let i = 0; i < currentSpelling.length; i++) {
    if (i >= correctSpelling.length) {
      newSpelling += REVEALED_EMPTY_CHAR;
    } else {
      newSpelling += currentSpelling[i];
    }
  }

  return newSpelling;
}

// Attempts to perform a backspace on [currentSpelling] from [caretPos], and
// returns the result.
export function tryBackspace(
  currentSpelling: string,
  caretPos: number,
): { newSpelling: string; newCaretPos: number } {
  if (caretPos === 0) {
    return { newSpelling: currentSpelling, newCaretPos: caretPos };
  }

  // If the caret is currently atop an exiting letter, delete that one.
  if (currentSpelling[caretPos] !== UNKNOWN_LETTER_CHAR) {
    return {
      newSpelling:
        currentSpelling.slice(0, caretPos) +
        UNKNOWN_LETTER_CHAR +
        currentSpelling.slice(caretPos + 1),
      newCaretPos: caretPos,
    };
  }

  // Otherwise, work backwards to find the next deletable character
  let deletePos = Math.max(caretPos - 1, 0);
  for (let i = deletePos; i >= 0; i--) {
    if (checkIsEditable(currentSpelling[i])) {
      deletePos = i;
      break;
    }
  }

  // Did not find a deletable character behind the caret
  if (deletePos === caretPos) {
    return { newSpelling: currentSpelling, newCaretPos: caretPos };
  }

  const newSpelling =
    currentSpelling.slice(0, deletePos) +
    UNKNOWN_LETTER_CHAR +
    currentSpelling.slice(deletePos + 1);

  return {
    newSpelling,
    newCaretPos: deletePos,
  };
}

// Returns true if the character was revealed by the user (ie is '!' or an
// uppercase letter)
export function checkIsRevealed(char: string): boolean {
  if (!char) {
    return false;
  }

  if (char === UNKNOWN_LETTER_CHAR) {
    return false;
  }

  if (char === REVEALED_EMPTY_CHAR) {
    return true;
  }

  if (char === char.toUpperCase()) {
    return true;
  }

  return false;
}

// Returns true if the character was typed by the player, as opposed to being
// empty or revealed by a clue.
export function checkIsTypedByPlayer(char: string): boolean {
  if (!char) {
    return false;
  }

  if (char === UNKNOWN_LETTER_CHAR) {
    return false;
  }

  if (char === REVEALED_EMPTY_CHAR) {
    return false;
  }

  if (char === char.toLowerCase()) {
    return true;
  }

  return false;
}

// Returns true if the character in question can be typed over.
export function checkIsEditable(char: string): boolean {
  return char === UNKNOWN_LETTER_CHAR || checkIsTypedByPlayer(char);
}
